import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEO from "@/components/SEO";
import CircuitField from "@/components/CircuitField";
import { Reveal, SectionTag } from "@/components/Reveal";
import { useSiteContent } from "@/context/ContentContext";

export default function FAQ() {
    const { faqs } = useSiteContent();
    return (
        <div data-testid="faq-page">
            <SEO
                title="FAQ | Camarca Digital Designs — Web Design Questions Answered"
                description="Answers to common questions about website design, logo design, branding, Facebook setup, pricing and how to get started with Camarca Digital Designs."
                path="/faq"
            />

            <div className="relative overflow-hidden">
                <CircuitField className="pointer-events-none absolute inset-0 h-full w-full opacity-40" />
                <header className="relative mx-auto max-w-7xl px-6 pb-16 pt-40 lg:px-10">
                    <Reveal>
                        <SectionTag>FAQ</SectionTag>
                        <h1 className="mt-6 max-w-4xl font-display text-4xl font-black leading-tight tracking-tight md:text-6xl">
                            Questions,{" "}
                            <span className="bg-gradient-to-r from-neon to-deep bg-clip-text text-transparent">answered.</span>
                        </h1>
                    </Reveal>
                </header>
            </div>

            <section className="relative mx-auto max-w-3xl px-6 pb-24 lg:px-0">
                <div className="bg-scanlines pointer-events-none absolute inset-0" aria-hidden="true" />
                <Reveal>
                    <Accordion type="single" collapsible data-testid="faq-accordion" className="relative">
                        {faqs.map((f, i) => (
                            <AccordionItem key={i} value={`item-${i}`} className="border-b border-white/10">
                                <AccordionTrigger
                                    data-testid={`faq-trigger-${i}`}
                                    className="gap-6 py-7 text-left hover:text-neon hover:no-underline"
                                >
                                    <span className="flex items-baseline gap-6">
                                        <span className="font-mono text-sm text-neon">{String(i + 1).padStart(2, "0")}</span>
                                        <span className="font-display text-base font-bold md:text-lg">{f.q}</span>
                                    </span>
                                </AccordionTrigger>
                                <AccordionContent data-testid={`faq-content-${i}`} className="pl-12 text-base leading-relaxed text-zinc-400">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Reveal>

                <Reveal className="mt-16">
                    <div className="flex flex-wrap items-center justify-between gap-6 border border-white/10 bg-surface p-8">
                        <div>
                            <h2 className="font-display text-xl font-bold">Still have a question?</h2>
                            <p className="mt-2 text-sm text-zinc-400">Send it over — every message gets a personal reply.</p>
                        </div>
                        <Link
                            to="/contact"
                            data-testid="faq-contact-cta"
                            className="group flex items-center gap-3 border border-neon/70 px-6 py-3.5 font-mono text-xs tracking-[0.2em] uppercase text-neon transition-[background-color,color,box-shadow] duration-300 hover:bg-neon hover:text-void hover:shadow-[0_0_20px_rgba(0,240,255,0.4)]"
                        >
                            Contact Me
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
